import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import type { OwnCatalogEntry, SharedCatalogEntry } from "../../shared/api/requests";
import { useT } from "../../shared/i18n";

/** One selectable destination catalog entry. Shared and rendered `name@owner`. */
export interface DestinationEntryChoice {
  id: string;
  label: string;
  /** Owner of a shared entry; undefined for the caller's own entries. */
  owner?: string;
}

/**
 * The entry list shared by the single-container move and the bulk move:
 * own entries plus writable shared entries, shared rendered `name@owner`.
 * Read-only shares never reach this list.
 *
 * @param entries own catalog entries, in server order.
 * @param sharedWithMe entries other owners shared with the caller.
 * @returns the choices in rendering order.
 */
export function destinationEntryChoices(
  entries: readonly OwnCatalogEntry[],
  sharedWithMe: readonly SharedCatalogEntry[],
): DestinationEntryChoice[] {
  return [
    ...entries.map((entry) => ({ id: entry.id, label: entry.name, owner: undefined })),
    ...sharedWithMe
      .filter((share) => share.level === "write" || share.level === "admin")
      .map((share) => ({ id: share.entry.id, label: `${share.entry.name}@${share.owner}`, owner: share.owner })),
  ];
}

interface DestinationEntryListProps {
  choices: readonly DestinationEntryChoice[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Prefix for per-row and empty-state test ids. */
  testIdPrefix: string;
  /** The entry the batch starts from; marked as the current destination. */
  currentEntryId?: string;
  /**
   * Owner of the source home (undefined for the caller's own home). A choice
   * with another owner crosses homes and is an ownership transfer; the chip is
   * shown for both own→shared and shared→own destinations.
   */
  sourceOwner?: string;
}

/**
 * Step one of every move flow: choose one destination catalog entry. The
 * selection is local to the dialog and confirmed with a Next action, so a
 * pre-selected current entry [`currentEntryId`] stays visible and changeable.
 */
export function DestinationEntryList({
  choices,
  selectedId,
  onSelect,
  testIdPrefix,
  currentEntryId,
  sourceOwner,
}: DestinationEntryListProps) {
  const t = useT();
  if (choices.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-empty`}>
        {t("moveNoEntries")}
      </Typography>
    );
  }
  return (
    <List data-testid={`${testIdPrefix}-list`}>
      {choices.map((choice) => (
        <ListItem key={choice.id} disablePadding>
          <ListItemButton
            selected={selectedId === choice.id}
            data-testid={`${testIdPrefix}-${choice.label}`}
            onClick={() => onSelect(choice.id)}
          >
            <ListItemText
              primary={choice.label}
              secondary={choice.id === currentEntryId ? t("bulkDestinationCurrent") : undefined}
            />
            {choice.owner !== sourceOwner && (
              <Chip size="small" color="warning" label={t("bulkTransferChip")} sx={{ ml: 1 }} />
            )}
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}
